import { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, DollarSign, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface PayPalFundsProps {
  onSuccess: () => void;
  giftGroupId?: string | null;
  recommendedAmount?: number | null;
}

// PayPal SDK types

interface PayPalOnApproveData {
  orderID: string;
}

interface PayPalButton {
  render: (container: string) => void;
}

declare global {
  interface Window {
    paypal?: {
      Buttons: (config: {
        createOrder: () => Promise<string>;
        onApprove: (data: PayPalOnApproveData) => Promise<void>;
        onError?: (err: Error) => void;
        style?: {
          layout?: string;
          color?: string;
          shape?: string;
          label?: string;
        };
      }) => PayPalButton;
    };
  }
}

export function PayPalFunds({ onSuccess, giftGroupId, recommendedAmount }: PayPalFundsProps) {
  // Use recommendedAmount if provided, otherwise default to empty string
  const [customAmount, setCustomAmount] = useState<string>(
    recommendedAmount ? recommendedAmount.toString() : ''
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const paypalButtonsRef = useRef<HTMLDivElement>(null);
  const paypalButtonsRendered = useRef(false);
  const customAmountRef = useRef<string>(recommendedAmount ? recommendedAmount.toString() : '');

  const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID_SANDBOX || import.meta.env.VITE_PAYPAL_CLIENT_ID;

  // Load PayPal SDK
  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) {
      setSdkError('PayPal is not configured. Please contact support.');
      return;
    }

    // Remove old SDK script if it exists (to force reload with new parameters)
    const existingScript = document.querySelector(
      `script[src^="https://www.paypal.com/sdk/js"]`
    );
    if (existingScript) {
      existingScript.remove();
      delete window.paypal; // Clear old SDK from window
    }

    // Load PayPal SDK script with venmo enabled
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&enable-funding=venmo`;
    script.async = true;
    
    script.onload = () => {
      setSdkLoaded(true);
      paypalButtonsRendered.current = false; // Reset when SDK loads
    };
    
    script.onerror = () => {
      setSdkError('Failed to load PayPal. Please refresh the page.');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount
      const scriptToRemove = document.querySelector(
        `script[src^="https://www.paypal.com/sdk/js"]`
      );
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [PAYPAL_CLIENT_ID]);

  // Render PayPal buttons when SDK is loaded
  useEffect(() => {
    if (!sdkLoaded || !window.paypal || !paypalButtonsRef.current) {
      return;
    }

    // Don't re-render if already rendered
    if (paypalButtonsRendered.current) {
      return;
    }

    try {
      const buttons = window.paypal.Buttons({
        createOrder: async () => {
          console.log('🔵 createOrder called');
          setIsProcessing(true);
          
          // Validate amount when user clicks PayPal button - use ref to get current value
          const selectedAmount = parseFloat(customAmountRef.current);
          console.log('💰 Amount from ref:', selectedAmount);
          
          if (!selectedAmount || selectedAmount < 1 || selectedAmount > 10000) {
            toast.error('Invalid amount', {
              description: 'Please enter an amount between $1.00 and $10,000.00',
            });
            setIsProcessing(false);
            throw new Error('Invalid amount');
          }
          
          try {
            console.log('📞 Calling backend createPayPalOrder...');
            // Call backend to create PayPal order
            const response = await api.createPayPalOrder(selectedAmount);
            
            console.log('✅ Backend response:', response);
            
            if (!response.success || !response.orderId) {
              throw new Error('Failed to create PayPal order');
            }

            toast.success('PayPal order created', {
              description: 'Complete the payment in the popup',
            });

            console.log('🎫 Returning orderId:', response.orderId);
            return response.orderId;
          } catch (error) {
            console.error('❌ Create order error:', error);
            toast.error('Failed to create payment', {
              description: 'Please try again or contact support',
            });
            setIsProcessing(false);
            throw error;
          }
        },
        onApprove: async (data) => {
          try {
            // Call backend to capture payment
            const response = await api.capturePayPalPayment(data.orderID);

            if (!response.success) {
              throw new Error('Failed to capture payment');
            }

            toast.success('Payment successful!', {
              description: `Added $${response.amount.toFixed(2)} to your balance`,
            });

            // If this is for a specific gift group, automatically contribute
            if (giftGroupId && response.amount) {
              try {
                const contributeResponse = await api.contributeToGiftGroup(giftGroupId, response.amount);
                
                toast.success('Contribution complete!', {
                  description: `Successfully contributed $${contributeResponse.contributedAmount.toFixed(2)} to the gift group`,
                });
              } catch (contributeError) {
                console.error('Auto-contribute error:', contributeError);
                toast.error('Funds added, but contribution failed', {
                  description: 'Please go to the gift group to contribute manually',
                });
              }
            }

            // Reset form
            setCustomAmount('');
            customAmountRef.current = '';
            setIsProcessing(false);

            // Notify parent to refresh balance
            onSuccess();
          } catch (error) {
            console.error('Capture payment error:', error);
            toast.error('Payment processing failed', {
              description: 'Please contact support if you were charged',
            });
            setIsProcessing(false);
          }
        },
        onError: (err) => {
          console.error('PayPal button error:', err);
          toast.error('PayPal error', {
            description: 'Please try again or contact support',
          });
          setIsProcessing(false);
        },
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
        },
      });

      buttons.render('#paypal-button-container');
      paypalButtonsRendered.current = true;
    } catch (error) {
      console.error('PayPal buttons render error:', error);
      setSdkError('Failed to initialize PayPal buttons');
    }
  }, [sdkLoaded, onSuccess, giftGroupId]); // Removed customAmount from dependencies

  const selectedAmount = parseFloat(customAmount);
  const isValidAmount = selectedAmount >= 1 && selectedAmount <= 10000;

  if (sdkError) {
    return (
      <Card className="p-6 border-destructive/50 bg-destructive/5">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-destructive mt-0.5" />
          <div>
            <div className="text-destructive mb-1">PayPal Unavailable</div>
            <div className="text-sm text-muted-foreground">{sdkError}</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-primary/20 shadow-lg">
      <div className="mb-4">
        <h3 className="text-primary mb-2">Add Funds via PayPal</h3>
        <p className="text-sm text-muted-foreground">
          {giftGroupId 
            ? 'Add funds and automatically contribute to your gift group'
            : 'Top up your credit balance to contribute to gift groups'
          }
        </p>
      </div>

      {/* Two Column Layout: Amount Input (Left) | PayPal Buttons (Right) */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column: Amount Input */}
        <div className="space-y-4">
          <Label htmlFor="amount-input">Amount to Add</Label>
          
          {/* Amount Input with currency formatting */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </div>
            <Input
              id="amount-input"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={customAmount}
              onChange={(e) => {
                // Allow only numbers and decimal point
                let newValue = e.target.value.replace(/[^\d.]/g, '');
                
                // Prevent multiple decimal points
                const parts = newValue.split('.');
                if (parts.length > 2) {
                  newValue = parts[0] + '.' + parts.slice(1).join('');
                }
                
                // Limit to 2 decimal places
                if (parts[1] && parts[1].length > 2) {
                  newValue = parts[0] + '.' + parts[1].slice(0, 2);
                }
                
                setCustomAmount(newValue);
                customAmountRef.current = newValue; // Update ref with current value
              }}
              disabled={isProcessing}
              className="pl-9 text-lg"
            />
          </div>

          {/* Amount Validation Message */}
          {customAmount && !isValidAmount && (
            <p className="text-sm text-destructive">
              Amount must be between $1.00 and $10,000.00
            </p>
          )}
          
          {/* Helper Text */}
          {!customAmount && (
            <p className="text-sm text-muted-foreground">
              Enter an amount to see payment options
            </p>
          )}
        </div>

        {/* Right Column: PayPal Buttons */}
        <div>
          {!sdkLoaded ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading PayPal...
            </div>
          ) : (
            <div>
              <div 
                id="paypal-button-container" 
                ref={paypalButtonsRef}
                className="min-h-[50px]"
              />
              {isProcessing && (
                <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing payment...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 pt-6 border-t border-primary/20 text-xs text-muted-foreground">
        <DollarSign className="w-4 h-4 inline mr-1" />
        Funds are added to your credit balance immediately after payment confirmation
      </div>
    </Card>
  );
}